/**
 * Spotify Deep Link Listener - Captures OAuth callback on native apps
 * Listens for youbetterrun://oauth/spotify/callback deep link
 */

import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';
import { getOAuthData, clearSpotifyOAuthData } from '@/lib/oauth-storage';

export const SpotifyDeepLinkListener = () => {
  const { toast } = useToast();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      logger.info('SpotifyDeepLinkListener: Skipping on web platform');
      return;
    }

    logger.info('🎵 Registering Spotify deep link listener');

    let listenerHandle: any;

    const registerListener = async () => {
      listenerHandle = await CapacitorApp.addListener('appUrlOpen', async (data) => {
        logger.info('📱 Deep link received', { url: data.url });

          if (data.url.includes('oauth/spotify/callback')) {
            try {
              persistentLogger.addLog('OAUTH', 'info', 'Spotify OAuth callback received', { 
                url: data.url.substring(0, 100) + '...',
                platform: Capacitor.getPlatform() 
              });
              
              // ✅ FIX: Parse custom URL scheme directly instead of using new URL()
              logger.info('📱 Deep link received', { url: data.url, platform: Capacitor.getPlatform() });
              
              const queryString = data.url.split('?')[1] || '';
              const urlParams = new URLSearchParams(queryString);
              const code = urlParams.get('code');
              const state = urlParams.get('state');
              const error = urlParams.get('error');

              logger.info('🎵 Spotify OAuth callback intercepted', { hasCode: !!code, hasState: !!state, error });

              if (error) {
                logger.error('❌ Spotify OAuth error', { error });
                persistentLogger.addLog('OAUTH', 'error', 'Spotify OAuth returned error', { error });
                toast({ title: 'Erro no Spotify', description: error === 'access_denied' ? 'Você cancelou a autorização do Spotify' : 'Erro ao conectar com Spotify', variant: 'destructive' });
                return;
              }

              if (!code) {
                logger.error('❌ No authorization code in callback');
                persistentLogger.addLog('OAUTH', 'error', 'No authorization code received', { url: data.url });
                toast({ title: 'Erro no Spotify', description: 'Código de autorização não recebido', variant: 'destructive' });
                return;
              }

              // 🔍 DIAGNÓSTICO: Log antes de tentar recuperar dados
              logger.info('🔍 About to retrieve OAuth data from Preferences', {
                platform: Capacitor.getPlatform(),
                isNative: Capacitor.isNativePlatform()
              });

              // ✅ Recuperar dados do OAuth usando Preferences API
              const storedState = await getOAuthData('spotify_state');
              const codeVerifier = await getOAuthData('spotify_code_verifier');

              // 🔍 DIAGNÓSTICO: Comparar Preferences vs localStorage
              const localStorageVerifier = localStorage.getItem('spotify_code_verifier');
              const localStorageState = localStorage.getItem('spotify_state');
              
              logger.info('🔍 OAuth storage diagnostic', { 
                preferencesVerifier: !!codeVerifier,
                preferencesVerifierLength: codeVerifier?.length || 0,
                preferencesState: !!storedState,
                localStorageVerifier: !!localStorageVerifier,
                localStorageVerifierLength: localStorageVerifier?.length || 0,
                localStorageState: !!localStorageState,
                hasStoredState: !!storedState, 
                hasReceivedState: !!state,
                statesMatch: storedState === state,
                hasCodeVerifier: !!codeVerifier,
                platform: Capacitor.getPlatform()
              });

              if (!codeVerifier) {
                // ✅ FALLBACK: Verificar se há tentativa pendente e oferecer retry
                const authAttempt = await getOAuthData('spotify_auth_attempt');
                
                logger.error('❌ Missing code_verifier - OAuth data lost', {
                  authAttemptTimestamp: authAttempt,
                  possibleCause: 'App killed by iOS during OAuth',
                  preferencesChecked: true,
                  localStorageChecked: true
                });

                if (authAttempt) {
                  const attemptTime = new Date(authAttempt).toLocaleTimeString();
                  toast({
                    title: 'Sessão Interrompida',
                    description: `O iOS fechou o app durante a autorização (${attemptTime}). Volte para Configurações e tente novamente.`,
                    variant: 'destructive',
                    duration: 8000
                  });
                } else {
                  toast({ 
                    title: 'Erro de Sessão', 
                    description: 'Sessão OAuth expirada. Tente novamente em Configurações > Integrações.', 
                    variant: 'destructive',
                    duration: 6000
                  });
                }
                
                // Navegar de volta para configurações após 2s
                setTimeout(() => {
                  window.location.href = '/settings/integrations';
                }, 2000);
                
                return;
              }

            // ⚠️ State validation: warn but don't block (some OAuth flows may not preserve state)
            if (storedState && state && storedState !== state) {
              logger.warn('⚠️ State mismatch in OAuth callback (continuing anyway)', {
                stored: storedState,
                received: state
              });
            }

            logger.info('✅ OAuth state validated, exchanging code for tokens');

            const platform = Capacitor.getPlatform();
            const redirectUri = platform === 'ios' || platform === 'android' ? 'youbetterrun://oauth/spotify/callback' : `${window.location.origin}/oauth/spotify/callback`;

            const { data: tokenData, error: tokenError } = await supabase.functions.invoke('spotify-oauth-callback', { body: { code, code_verifier: codeVerifier, redirect_uri: redirectUri } });

            if (tokenError) {
              logger.error('❌ Token exchange failed', tokenError);
              toast({ title: 'Erro ao Conectar', description: 'Falha ao obter tokens do Spotify. Tente novamente.', variant: 'destructive' });
              return;
            }

            logger.info('✅ Spotify tokens obtained successfully');

            // Limpar todos os dados do OAuth usando persistência nativa
            await clearSpotifyOAuthData();

            toast({ title: 'Spotify Conectado!', description: 'Sua conta do Spotify foi conectada com sucesso.' });

            window.dispatchEvent(new CustomEvent('spotifyAuthSuccess'));
            
            // ✅ Navegar para a tela de perfil após sucesso
            setTimeout(() => {
              window.location.href = '/profile';
            }, 1500);
          } catch (error) {
            logger.error('❌ Error processing Spotify callback', error);
            toast({ title: 'Erro no Spotify', description: 'Erro ao processar callback do Spotify', variant: 'destructive' });
          }
        }
      });
    };

    registerListener();

    return () => {
      logger.info('🧹 Cleaning up Spotify deep link listener');
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [toast]);

  return null;
};
